import RefetchContent from '@/app/components/feedback/RefetchContent';
import Skeleton from '@/app/components/feedback/Skeleton';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ANNOUNCEMENTS_PAGE_URL } from '@/api/core/links';
import { type AnnouncementRecipientDto } from '@/app/models/AnnouncementRecipientDto';
import { type NotificationDto } from '@/app/models/NotificationDto';
import AnnouncementTabPanel from '@/app/modules/announcements/components/AnnouncementTabPanel';
import DailySummarySections from '@/app/modules/notifications/components/DailySummarySections';
import { getNotificationSourceUrl } from '@/app/modules/notifications/core/_utils';
import TaskTabPanel from '@/app/modules/task/components/TaskTabPanel';
import { KTIcon } from '@/_metronic/helpers';

export type NotificationsTab = 'notifications' | 'tasks' | 'announcements';

const RESOLVED = { iconClass: 'btn-light-success', badgeClass: 'badge-light-success', label: 'Resolvida' };
const URGENT = { iconClass: 'btn-danger', badgeClass: 'badge-light-danger', label: 'Urgente' };

// O resto do resumo fica no modal que o clique abre.
const SUMMARY_PREVIEW_SECTIONS = 2;

// O aviso de urgente resolvido também chega como urgente: resolvida vence.
const getNotificationStatus = (notification: NotificationDto) => {
  if (notification.resolved_date) return RESOLVED;
  if (notification.urgent) return URGENT;
  return undefined;
};

interface NotificationsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: NotificationDto[];
  loading: boolean;
  onNotificationRead: (id: string) => void;
  onSummaryOpen: (notification: NotificationDto) => void;
  tab: NotificationsTab;
  onTabChange: (tab: NotificationsTab) => void;
  /** Tarefas em andamento e finalizadas ainda não vistas: a contagem da aba Tarefas. */
  runningTasks: number;
  unseenTasks: number;
  /** Comunicados que ainda pedem a pessoa: a contagem da aba Comunicados. */
  pendingAnnouncements: number;
  onAnnouncementOpen: (announcement: AnnouncementRecipientDto) => void;
  /**
   * Boundary used to detect "click outside". Should wrap both the trigger
   * button and this dropdown so clicking the trigger doesn't close-then-reopen.
   */
  containerRef?: React.RefObject<HTMLElement | null>;
}

const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({
  isOpen,
  onClose,
  notifications,
  loading,
  onNotificationRead,
  onSummaryOpen,
  tab,
  onTabChange,
  runningTasks,
  unseenTasks,
  pendingAnnouncements,
  onAnnouncementOpen,
  containerRef,
}) => {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleNotificationClick = (notification: NotificationDto) => {
    if (notification.id) onNotificationRead(notification.id);
    if (notification.summary?.length) {
      onSummaryOpen(notification);
      onClose();
      return;
    }
    const sourceUrl = getNotificationSourceUrl(notification);
    if (!sourceUrl) return;
    navigate(sourceUrl);
    onClose();
  };

  const handleSummaryLinkClick = (notification: NotificationDto) => {
    if (notification.id) onNotificationRead(notification.id);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Use the container (trigger + dropdown) as the boundary so clicking the
      // trigger button is not treated as an outside click. Falls back to the
      // dropdown panel when no container ref is provided.
      const boundary = containerRef?.current ?? dropdownRef.current;
      if (boundary && !boundary.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, containerRef]);

  const handleViewAll = () => {
    navigate('/notificacao/lista');
    onClose();
  };

  const handleAnnouncementOpen = (announcement: AnnouncementRecipientDto) => {
    onAnnouncementOpen(announcement);
    onClose();
  };

  const handleViewAllAnnouncements = () => {
    navigate(ANNOUNCEMENTS_PAGE_URL);
    onClose();
  };

  const getTimeAgo = (dateString?: string) => {
    if (!dateString) return 'Data não disponível';

    const now = new Date();
    const notificationDate = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'agora mesmo';
    if (diffInMinutes < 60) return `${diffInMinutes} minutos atrás`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return diffInHours === 1 ? 'há 1 hora' : `há ${diffInHours} horas`;

    const diffInDays = Math.floor(diffInHours / 24);
    return diffInDays === 1 ? 'há 1 dia' : `há ${diffInDays} dias`;
  };

  const getNotificationIcon = (type?: string) => {
    switch (type) {
      // FINANCEIRO
      case 'PAYMENT_RECEIVED':
        return 'dollar';
      case 'FINANCIAL_TRANSACTION':
        return 'credit-card';
      case 'EXPIRED_INVOICE':
        return 'bill';
      case 'BUDGET_LIMIT':
        return 'chart-line-down';
      case 'CREDIT_LIMIT':
        return 'wallet';
      case 'PRICE_CHANGE':
        return 'price-tag';

      // CLIENTE
      case 'NEW_CLIENT':
        return 'user-plus';
      case 'CLIENT_FEEDBACK':
        return 'message-text-2';
      case 'CLIENT_BIRTHDAY':
        return 'cake';
      case 'NEW_OPPORTUNITY':
        return 'star';
      case 'PROFILE_UPDATE':
        return 'user-edit';

      // ESTOQUE
      case 'LOW_STOCK':
        return 'warning-2';
      case 'EXPIRED_PRODUCT':
        return 'time';
      case 'HIGH_DEMAND':
        return 'arrow-up';
      case 'NEW_SUPPLIER':
        return 'truck';

      // VENDAS
      case 'NEW_SALE':
        return 'basket';
      case 'ORDER_CANCELED':
        return 'cross-circle';
      case 'SENT_ORDER':
        return 'send';
      case 'GOAL_REACHED':
        return 'medal';

      // ENTREGA
      case 'LATE_DELIVERY':
        return 'clock';
      case 'EQUIPMENT_MAINTENANCE':
        return 'wrench';

      case 'DAILY_SUMMARY':
        return 'chart-simple';

      // SISTEMA
      case 'SYSTEM_UPDATE':
        return 'code';
      case 'NEW_FEATURE_AVAILABLE':
        return 'rocket';
      case 'ACCESS_NOT_AUTHORIZED':
        return 'shield-cross';
      case 'NEW_INTEGRATION':
        return 'connect';
      case 'POWER_SURGE':
        return 'flash';

      // GESTÃO/ADMINISTRAÇÃO
      case 'SCHEDULED_MEETING':
        return 'calendar';
      case 'ASSIGNED_TASK':
        return 'task-square';
      case 'NEW_PROJECT':
        return 'folder-plus';
      case 'STAFF_ABSENCE':
        return 'user-minus';
      case 'SCHEDULED_AUDIT':
        return 'search-list';
      case 'NEW_TRAINING':
        return 'book-open';
      case 'CONTRACT_CHANGE':
        return 'document';

      // DOCUMENTOS/COMPLIANCE
      case 'NEW_DOCUMENT':
        return 'file-plus';
      case 'LEGISLATION_UPDATE':
        return 'law';
      case 'EXPIRE_CERTIFICATE':
        return 'certificate';
      case 'REPORT_DONE':
        return 'chart-bar';

      // COMUNICAÇÃO/SUPORTE
      case 'NEW_COMMENT':
        return 'message';
      case 'NEW_SUPPORT_TICKET':
        return 'support';

      // PRODUÇÃO
      case 'PRODUCTION_ISSUE':
        return 'exclamation-triangle';

      // PADRÕES ANTIGOS (compatibilidade)
      case 'SUCCESS':
        return 'check-circle';
      case 'WARNING':
        return 'warning';
      case 'ERROR':
        return 'cross-circle';
      case 'INFO':
      default:
        return 'information';
    }
  };

  if (!isOpen) return null;

  const emptyNotifications = loading ? (
    <div className="px-6 py-4">
      <output className="visually-hidden">Carregando notificações...</output>
      {[0, 1, 2].map((notificationIndex) => (
        <div key={notificationIndex} className="d-flex align-items-center gap-4 py-4">
          <Skeleton className="rounded flex-shrink-0" height={44} width={44} />
          <div className="flex-grow-1">
            <Skeleton className="d-block mb-2" height={20} width="75%" />
            <Skeleton className="d-block" height={30} width="90%" />
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="text-center py-8">
      <KTIcon className="fs-3x text-muted mb-3" iconName="notification" />
      <p className="text-muted mb-0">Nenhuma notificação encontrada</p>
    </div>
  );

  const isRefreshing = loading && notifications.length > 0;

  return (
    <div
      ref={dropdownRef}
      className="position-absolute bg-body shadow-lg border rounded-3 p-0"
      style={{
        top: '100%',
        right: '0',
        width: 'min(350px, calc(100vw - 32px))',
        zIndex: 1050,
        marginTop: '8px',
      }}
    >
      <ul className="nav nav-line-tabs nav-line-tabs-2x nav-stretch fw-semibold px-6 mb-0" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            aria-controls="notifications-panel-notifications"
            aria-selected={tab === 'notifications'}
            className={clsx('nav-link bg-transparent py-4', tab === 'notifications' && 'active')}
            id="notifications-tab-notifications"
            role="tab"
            type="button"
            onClick={() => onTabChange('notifications')}
          >
            Notificações
            {notifications.length > 0 ? (
              <span className="badge badge-light-danger ms-2">{notifications.length}</span>
            ) : null}
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            aria-controls="notifications-panel-tasks"
            aria-selected={tab === 'tasks'}
            className={clsx('nav-link bg-transparent py-4', tab === 'tasks' && 'active')}
            id="notifications-tab-tasks"
            role="tab"
            type="button"
            onClick={() => onTabChange('tasks')}
          >
            Tarefas
            {runningTasks > 0 ? <span className="badge badge-light-primary ms-2">{runningTasks}</span> : null}
            {runningTasks === 0 && unseenTasks > 0 ? (
              <span className="badge badge-light-success ms-2">{unseenTasks}</span>
            ) : null}
          </button>
        </li>
        <li className="nav-item">
          <button
            aria-controls="notifications-panel-announcements"
            aria-selected={tab === 'announcements'}
            className={clsx('nav-link bg-transparent py-4', tab === 'announcements' && 'active')}
            id="notifications-tab-announcements"
            role="tab"
            type="button"
            onClick={() => onTabChange('announcements')}
          >
            Comunicados
            {pendingAnnouncements > 0 ? (
              <span className="badge badge-light-danger ms-2">{pendingAnnouncements}</span>
            ) : null}
          </button>
        </li>
      </ul>

      {tab === 'tasks' ? <TaskTabPanel /> : null}

      {tab === 'announcements' ? (
        <AnnouncementTabPanel onAnnouncementOpen={handleAnnouncementOpen} onViewAll={handleViewAllAnnouncements} />
      ) : null}

      <div
        aria-busy={loading}
        aria-labelledby="notifications-tab-notifications"
        className={clsx(tab !== 'notifications' && 'd-none')}
        id="notifications-panel-notifications"
        role="tabpanel"
      >
        {/* Header */}
        <div className="px-6 py-4 border-bottom bg-light-secondary">
          <div className="d-flex align-items-center justify-content-between">
            <p className="text-muted fw-semibold fs-7 mb-0">{notifications.length} não lidas</p>
            <button className="btn btn-sm btn-secondary fw-bold" type="button" onClick={handleViewAll}>
              Ver todas
            </button>
          </div>
        </div>

        {/* Content */}
        <RefetchContent loading={isRefreshing}>
          <div className="bg-light-secondary" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {notifications.length === 0 ? emptyNotifications : null}

            {notifications.map((notification) => {
              const status = getNotificationStatus(notification);
              const hiddenSections = (notification.summary?.length ?? 0) - SUMMARY_PREVIEW_SECTIONS;
              return (
                // O botão cobre o item (stretched-link) sem envolvê-lo: o link do resumo não pode morar dentro dele.
                <div
                  key={notification.id}
                  className={clsx(
                    'position-relative px-6 py-4',
                    notification.active && 'bg-light-secondary',
                    'hover-bg-light-primary',
                  )}
                >
                  <div className="d-flex align-items-center">
                    <div className="me-4">
                      <span className={clsx('btn btn-icon', status?.iconClass ?? 'btn-primary')}>
                        <KTIcon className="fs-2" iconName={getNotificationIcon(notification.type?.value)} />
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <button
                        className="stretched-link d-block w-100 text-start border-0 bg-transparent p-0"
                        type="button"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <span className="d-flex align-items-center justify-content-between mb-1">
                          <span className="fw-semibold text-gray-800 fs-6">{notification.title}</span>
                          <span className="badge badge-light-info fs-7">
                            {getTimeAgo(notification.registration_date)}
                          </span>
                        </span>
                        {status ? (
                          <span className={clsx('badge fs-8 mb-1', status.badgeClass)}>{status.label}</span>
                        ) : null}
                      </button>
                      {notification.summary?.length ? (
                        <>
                          <DailySummarySections
                            sections={notification.summary.slice(0, SUMMARY_PREVIEW_SECTIONS)}
                            onNavigate={() => handleSummaryLinkClick(notification)}
                          />
                          {hiddenSections > 0 ? (
                            <span className="d-block text-primary fs-8 mt-2">
                              Mais {hiddenSections} {hiddenSections === 1 ? 'seção' : 'seções'}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <p
                          className="text-muted fs-7 mb-0"
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: '1.4em',
                            maxHeight: '2.8em',
                          }}
                        >
                          {notification.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </RefetchContent>
      </div>
    </div>
  );
};

export default NotificationsDropdown;
